import { useMemo } from "react"
import "./index.scss"
import { dynamicSortMultiple } from "utils/scripts/sort"
import { displaySampleId } from "utils/scripts/sampleId"
import { DateTime } from "luxon"
import Table from "react-bootstrap/Table"
import InputGroup from "react-bootstrap/InputGroup"
import { useSelector } from "react-redux"
import { FiFlag } from "react-icons/fi"
import useT from "utils/hooks/useT"
// import { teamStatus } from "state_management"

export { default as SampleTableForPlannedAnalysis } from "./SampleTableForPlannedAnalysis"
export { default as SampleTableForUnplannedAnalysis } from "./SampleTableForUnplannedAnalysis"

// A table displaying samples.
// planned prop is used to conditional render between samples that are planned vs unplanned
// 'analysisId is only used for planned and plannable tables to find illegal rows
export default function SampleTable({ popupSamples, checkedSamples, setCheckedSamples, analysisId, onUnflag = null }) {
  const { t } = useT("common")
  // Sorting after three parameters. Depends on 'priorityMode'.
  const priorityMode = useSelector((state) => state.teamStatus.sortingParameter)
  const transformDeadlines = (samples, timezone) => {
    return samples.map(sample => ({
      ...sample,
      transformedDeadline: DateTime.fromISO(sample.deadline).setZone(timezone).toISO()
    }));
  };
  const teamTimeZone = useSelector((state) => state.teamInfo.timezone)
  const sortedPopupSamples = useMemo(() => {
    const transformedSamples = transformDeadlines(popupSamples, teamTimeZone);
    return transformedSamples.sort(dynamicSortMultiple(
      priorityMode === "DEADLINE" ? "transformedDeadline" : "priority",
      priorityMode === "DEADLINE" ? "priority" : "transformedDeadline",
      "sampleName"
    ));
  }, [popupSamples, priorityMode, teamTimeZone]);
  const iapFromDate = DateTime.fromISO(useSelector((state) => state.teamStatus.iapFromDate))

  // NOT used for unplanned sample-table. Used to check for illegal samples (yellow rows)
  const tasksForAnalysis = Object.values(useSelector((state) => state.analysisTasks)).filter(task => task.analysisId === analysisId)

  // SPEC-027 (RR4 #2): GMP-guard — once the analysis has any QC result it is "in progress", so its
  // samples may no longer be unplanned (removing one would drop its QC data); DONE samples are
  // likewise locked. LIMS-only; FAILED keeps its existing no-checkbox behaviour. Authoritative
  // enforcement is backend-side.
  const showLims = useSelector((state) => state.teamStatus.showLims)
  const analysisHasResults = useSelector(
    (state) => !!analysisId && state.limsResults.items.some((r) => r.analysisId === analysisId)
  )

  return (
    <>
      <Table bordered hover className="SampleTable">
        <thead>
          <tr>
            <th>{t("name")}</th>
            <th>{t("sampleId")}</th>
            <th>{t("productName")}</th>
            <th>{t("priority")}</th>
            <th>{t("arrival")}</th>
            <th>{t("deadline")}</th>
            <th>{t("status")}</th>
            {setCheckedSamples && <th>{t("selectSamples")}</th>}
          </tr>
        </thead>
        <tbody>
          {sortedPopupSamples.map((sample) => (
            <tr
              key={sample.id}
              className={
                DateTime.fromISO(sample.deadline).setZone(teamTimeZone) < iapFromDate
                  ? "sampleTable-row-overDeadline"
                  : analysisId && tasksForAnalysis.find((task) => DateTime.fromISO(sample.deadline).setZone(teamTimeZone) < DateTime.fromISO(task.end).setZone(teamTimeZone))
                    ? "sampleTable-row-illegal"
                    : ""
              }
              style={{ fontStyle: sample.status === "FAILED" ? "italic" : "normal", fontWeight: sample.priority === 1 ? "bold" : "normal" }}
            >
              <td>
                {sample.isFlagged === true && <SampleFlag sample={sample} onUnflag={onUnflag} t={t} />}
                {sample.sampleName}
              </td>
              <td>
                {
                  // #4145 — DISPLAY. A FAILED sample that handed its number to a rerun stores it
                  // with an invisible marker + uuid appended; the lab reads the number, never the
                  // uniqueness suffix. Non-LIMS too: `fail_samples` has always done the same to
                  // its FAILED copy, and this cell is where it showed.
                  `${setCheckedSamples ? (sample.status === "FAILED" ? "" : displaySampleId(sample.observationId)) : displaySampleId(sample.observationId)}${sample.exclusive ? "*" : ""}`
                }
              </td>
              <td className="sampletable-productName" title={sample.productName}>{sample.productName ? sample.productName : t("none")}</td>
              <td>{sample.priority}</td>
              <td className="sampletable-time-column">{DateTime.fromISO(sample.arrival).setZone(teamTimeZone).toFormat("d. MMM HH.mm")}</td>
              <td className="sampletable-time-column">{DateTime.fromISO(sample.deadline).setZone(teamTimeZone).toFormat("d. MMM HH.mm")}</td>
              <td>{sample.status}</td>
              {setCheckedSamples && (
                <td className="sampletable-checkbox-column">
                  {sample.status !== "FAILED" && (
                    showLims && (analysisHasResults || sample.status === "DONE") ? (
                      <span title={t("limsUnplanBlockedQc")} style={{ cursor: "not-allowed" }}>
                        <input type="checkbox" disabled />
                      </span>
                    ) : (
                      <InputGroup className="sample-checkBox">
                        <input
                          type="checkbox"
                          onClick={() => {
                            if (!checkedSamples.includes(sample.id)) {
                              setCheckedSamples([...checkedSamples, sample.id])
                            } else {
                              setCheckedSamples(checkedSamples.filter((id) => id !== sample.id))
                            }
                          }}
                        />
                      </InputGroup>
                    )
                  )}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </Table>
    </>
  )
}

// The flag in front of a flagged sample's name. #4225: where the caller passes `onUnflag`, the flag
// doubles as the "unflag" action.
function SampleFlag({ sample, onUnflag, t }) {
  if (!onUnflag) return <FiFlag style={{ marginRight: '5px' }} />
  return (
    <button
      type="button"
      className="sampletable-unflag"
      title={t("unflagSample")}
      aria-label={t("unflagSample")}
      onClick={() => onUnflag(sample)}
    >
      <FiFlag />
    </button>
  )
}
